import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormikProps } from 'formik';
import { cleanObj } from '../../../../../shared/utils/objects';
import { ToastSwal } from '../../../../components/general/toastSwals/toastSwal';
import { ISelect } from '../../../../../shared/types/shared';
import {
    CasesGeneral,
    CasesGenerallCallInfo,
    CasesGeneralClaims,
    CasesGeneralNotification,
    CasesGeneralTypification,
} from '../../../../../shared/types/modules/cases-general';
import {
    asyncCreateCasesGeneral,
    asyncGetCasesGeneral,
    asyncUpdateCasesGeneral,
} from '../../../../../services/cases-general.service';
import {
    CASES_GENERAL,
    CASES_GENERAL_DETAIL,
} from '../../../../../routes/paths';

export const useCasesGeneralForm = () => {
    const navigate = useNavigate();
    const { idAccount } = useParams();
    const { idCasesGeneral } = useParams();
    const [casesGeneral, setCasesGeneral] = useState<CasesGeneral>();
    const [loading, setLoading] = useState(false);
    const [formikCasesGeneral, setFormikCasesGeneral] =
        useState<FormikProps<CasesGeneral>>();
    const [formikCasesGeneraLClaims, setFormikCasesGeneralClaims] =
        useState<FormikProps<CasesGeneralClaims>>();
    const [formikCasesGeneraLNotification, setFormikCasesGeneralNotification] =
        useState<FormikProps<CasesGeneralNotification>>();
    const [formikCasesGeneraLTypification, setFormikCasesGeneralTypification] =
        useState<FormikProps<CasesGeneralTypification>>();
    const [formikCasesGeneraLCallInfo, setFormikCasesGeneralCallInfo] =
        useState<FormikProps<CasesGenerallCallInfo>>();

    const isValidForm = formikCasesGeneraLCallInfo?.isValid;

    const handleSubmit = async () => {
        await formikCasesGeneral?.submitForm();
        await formikCasesGeneraLCallInfo?.submitForm();
        await formikCasesGeneraLClaims?.submitForm();
        await formikCasesGeneraLNotification?.submitForm();
        await formikCasesGeneraLTypification?.submitForm();

        if (!isValidForm) {
            ToastSwal.fire({
                text: 'Por favor complete todos los campos obligatorios.',
                icon: 'warning',
                timer: 5000,
                position: 'center-end',
            });
            return;
        }

        setLoading(true);

        // Limpia y prepara los datos para el envío
        const data: CasesGeneral = {};
        data.account = casesGeneral?.account?.id || idAccount;
        data.callInfo = cleanObj(formikCasesGeneraLCallInfo?.values) || {};
        data.claims = cleanObj(formikCasesGeneraLClaims?.values) || {};
        data.notificationClient =
            cleanObj(formikCasesGeneraLNotification?.values) || {};
        data.typification =
            cleanObj(formikCasesGeneraLTypification?.values) || {};

        let res: CasesGeneral | undefined = undefined;

        try {
            if (casesGeneral && casesGeneral.id)
                res = await asyncUpdateCasesGeneral(casesGeneral.id, data);
            else res = await asyncCreateCasesGeneral(data);
        } catch (error) {
            console.error('Error al crear el caso:', error);
        }

        if (res) {
            ToastSwal.fire({
                text: `¡Felicidades! El caso fue ${
                    casesGeneral && casesGeneral.id ? 'actualizado' : 'creado'
                } satisfactoriamente.`,
                icon: 'success',
                timer: 5000,
                position: 'center-end',
            });
            navigate(`${CASES_GENERAL_DETAIL}/${res.id}`);
        } else {
            ToastSwal.fire({
                text: 'Lo sentimos, ocurrió un error al crear el caso. Por favor intente nuevamente más tarde.',
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
        }

        setLoading(false);
    };

    const handleClean = () => {
        formikCasesGeneral?.resetForm();
        formikCasesGeneraLCallInfo?.resetForm();
        formikCasesGeneraLNotification?.resetForm();
        formikCasesGeneraLTypification?.resetForm();
        formikCasesGeneraLClaims?.resetForm();
    };
    const handleBack = () => {
        navigate(-1);
    };

    useEffect(() => {
        if (Boolean(idCasesGeneral?.trim())) {
            setLoading(true);

            asyncGetCasesGeneral(idCasesGeneral.trim())
                .then((casesGeneralData) => {
                    if (!casesGeneralData || !casesGeneralData.id) {
                        navigate(CASES_GENERAL);
                        return;
                    }

                    // Procesa los objetos anidados y convierte los IDs en strings
                    [
                        casesGeneralData?.callInfo,
                        casesGeneralData?.claims,
                        casesGeneralData?.notificationClient,
                        casesGeneralData?.typification,
                    ].forEach((item: any) => {
                        if (item) {
                            Object.keys(item).forEach((key) => {
                                if (item[key] && (item[key] as ISelect)?.id) {
                                    item[key] = (
                                        item[key] as ISelect
                                    )?.id.toString();
                                }
                            });
                        }
                    });

                    setCasesGeneral(casesGeneralData);
                })
                .finally(() => setLoading(false));
        }
    }, [idCasesGeneral]);

    return {
        casesGeneral,
        idAccount,
        loading,
        isValidForm,
        handleSubmit,
        handleClean,
        handleBack,
        formikCasesGeneral,
        setFormikCasesGeneral,
        setFormikCasesGeneralClaims,
        setFormikCasesGeneralNotification,
        setFormikCasesGeneralTypification,
        setFormikCasesGeneralCallInfo,
    };
};
